import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User as UserIcon, Briefcase, Factory, ExternalLink, Truck, ShoppingCart, Package, Wrench, Boxes } from 'lucide-react';
import HCLogo from '../components/HCLogo';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';

const ROLES = [
  { key: 'employee',   icon: UserIcon,  labelKey: 'login.role.employee'   },
  { key: 'hr',         icon: Briefcase, labelKey: 'login.role.hr'         },
  { key: 'erp',        icon: Boxes,     labelKey: 'login.role.erp'        },
  { key: 'production', icon: Factory,   labelKey: 'login.role.production' },
];

const DASHBOARD_BY_ROLE = { hr: '/hr', production: '/production', erp: '/erp' };

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('employee');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('login.errors.missing'));
      return;
    }
    if (!email.includes('@')) {
      toast.error(t('login.errors.invalidEmail'));
      return;
    }
    setLoading(true);
    try {
      const u = await login(email, password, role);
      toast.success(t('login.success'));
      // Backend returns u.dashboard = which module the user chose in the login tab
      // (respecting access flags / admin). Fallback to actual role, then home.
      const target = DASHBOARD_BY_ROLE[u.dashboard] || DASHBOARD_BY_ROLE[u.role] || '/';
      navigate(target);
    } catch (err) {
      const msg = err?.response?.data?.detail || t('login.errors.generic');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="login-page">
      {/* Left - Branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-300 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <div className="flex items-center gap-3">
            <HCLogo size="lg" bgClass="bg-white/15 backdrop-blur ring-1 ring-white/20" />
            <div>
              <div className="text-xl font-semibold tracking-tight">HC Assistant</div>
              <div className="text-xs text-blue-100/80">{t('login.platformSubtitle')}</div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h1 className="text-5xl font-bold leading-tight tracking-tight">
                {t('login.brandTitle')}
              </h1>
              <p className="mt-4 text-blue-50 text-xl font-medium leading-snug max-w-lg">
                {t('login.brandTagline')}
              </p>
              <p className="mt-4 text-blue-100/90 text-lg leading-relaxed max-w-lg">
                {t('login.brandDescription')}
              </p>
              <ul className="mt-3 space-y-2 text-blue-100/95 text-lg leading-relaxed max-w-lg">
                <li className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-blue-200 flex-shrink-0" />
                  <span>{t('login.feat.logistics')}</span>
                </li>
                <li className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5 text-blue-200 flex-shrink-0" />
                  <span>{t('login.feat.ecommerce')}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-blue-200 flex-shrink-0" />
                  <span>{t('login.feat.packaging')}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Wrench className="w-5 h-5 text-blue-200 flex-shrink-0" />
                  <span>{t('login.feat.assembly')}</span>
                </li>
              </ul>
            </div>

            <a
              href="https://hc-srl.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-website-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-800 text-base font-semibold hover:bg-blue-50 transition-colors shadow-sm ring-1 ring-white/30"
            >
              {t('login.visitWebsite')}
              <ExternalLink className="w-[18px] h-[18px]" />
            </a>
          </div>

          <div className="text-xs text-blue-100/70">
            &copy; {new Date().getFullYear()} HC. Powered by Emergent AI.
          </div>
        </div>
      </div>

      {/* Right - Login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 lg:px-10 relative">
        {/* Language selector — top-right */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector variant="default" size="sm" />
        </div>

        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <HCLogo size="lg" />
            <div className="text-lg font-semibold text-slate-900">HC Assistant</div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{t('login.title')}</h2>
            <p className="mt-1.5 text-sm text-slate-500">
              {t('login.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('login.loginAs')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ROLES.map(r => {
                  const Icon = r.icon;
                  const isSelected = role === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      data-testid={`role-${r.key}-btn`}
                      onClick={() => setRole(r.key)}
                      className={`flex flex-col items-center justify-center gap-1 h-14 rounded-lg border text-xs font-medium transition-all ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50 text-blue-800 ring-2 ring-blue-500/10'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {t(r.labelKey)}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {t('login.roleHint', 'Seleziona l\'applicazione a cui vuoi accedere. Gli account autorizzati potranno cambiarla dopo il login.')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t('login.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t('login.passwordLabel')}</Label>
                <button type="button" className="text-xs font-medium text-blue-700 hover:text-blue-800">
                  {t('login.forgotPassword')}
                </button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-700 hover:bg-blue-800 text-white font-medium"
              data-testid="login-submit"
            >
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          <div className="mt-6 text-center text-xs text-slate-500">
            {t('login.terms')} <a href="#" className="text-blue-700 hover:underline">{t('login.termsLink')}</a> {t('login.and')} <a href="#" className="text-blue-700 hover:underline">{t('login.privacyLink')}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
